import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Holds the Manifests search text and mirrors it in the `search` URL param, so Back, refresh, and
 * shared links restore it. The first value is read from the URL; later changes replace the current
 * history entry and keep any other params.
 * @param {boolean} enabled When false the hook neither reads nor writes the URL (it is only local state).
 * @returns {[string, (term: string) => void]} The current term and its setter.
 */
export const useUrlSearchTerm = (enabled) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [term, setTermState] = useState(() => (enabled ? searchParams.get('search') || '' : ''));

    // A debounced onChange can fire after the page is gone; it must not rewrite the URL of wherever the user went.
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const setTerm = useCallback(
        (next) => {
            setTermState(next);
            if (!enabled || !mounted.current) return;
            const trimmed = next?.trim();
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    if (trimmed) params.set('search', trimmed);
                    else params.delete('search');
                    return params;
                },
                { replace: true }
            );
        },
        [enabled, setSearchParams]
    );

    return [term, setTerm];
};
